import React, { useContext, useMemo } from 'react';
import { Switch, Tag } from '@onward-delivery/design';
import { UserContext } from '@/components/App';
import { CorpAdminContext } from '@/components/CorporateUsers/store';
import { CopyableId } from '@/components/CorporateUsers/modals/blocks';
import { CardRow, SystemCard } from './blocks';
import TmsCard from './TmsCard';
import ShipperIntegrationsCard from './ShipperIntegrationsCard';
import { useIntegrationsFlag } from './hooks';
import { INTEGRATED_RELATIONSHIPS, hasConnectedIntegration, shipperIntegrationRowsFor } from './utils';

const NOTICE_CLASS = 'rounded-lg border border-slate-200 bg-white p-4 text-[13px]';

const PlatformCard = () => {
    const { client: carrier } = useContext(UserContext);
    const status = carrier.approved ? <Tag variant="primary">Live</Tag> : <Tag variant="warning">Pending approval</Tag>;

    return (
        <SystemCard
            badge="O"
            badgeClassName="bg-slate-900"
            title="Onward platform"
            subtitle="the master link — every order flows through this"
        >
            <CardRow label="Account ID">
                <CopyableId label="Account ID" value={carrier.client_id} />
            </CardRow>
            <CardRow label="Login">{carrier.email}</CardRow>
            <CardRow label="Status">{status}</CardRow>
        </SystemCard>
    );
};

const Integrations = () => {
    const { client: carrier, admin_clients } = useContext(UserContext);
    const { state, callbacks } = useContext(CorpAdminContext);
    const { defaultIntLoading, defaultIntError, intLoading } = state;

    const flag = useIntegrationsFlag({
        carrierId: carrier.client_id,
        organizationId: state.organizationId,
        notify: callbacks?.setNotification,
    });
    // Flex Centers and Fulfillment Partners; Motor Carriers skip integrations.
    const isIntegrated = INTEGRATED_RELATIONSHIPS.includes(flag.relationship);

    const orgShippers = useMemo(
        () => (admin_clients || []).filter((client) => !!client.user?.roles?.['SHIPPER']),
        [admin_clients]
    );
    const shipperRows = useMemo(
        () =>
            shipperIntegrationRowsFor({
                overrides: state.integrationOverrides,
                carrierId: carrier.client_id,
                shippersById: Object.fromEntries(orgShippers.map((shipper) => [shipper.client_id, shipper])),
            }),
        [carrier.client_id, orgShippers, state.integrationOverrides]
    );
    const defaultIntegrations = state.defaultIntegrationsByCarrier[carrier.client_id] || [];
    const connected = hasConnectedIntegration(defaultIntegrations, shipperRows);

    // Once everything it depends on has loaded: while the carrier has no integration at all (a TMS default or one with a
    // shipper), or while the flag is on, so it can still be turned off.
    const loaded = !flag.loading && !intLoading && !defaultIntLoading;
    const showsFlag = isIntegrated && loaded && (!connected || flag.noIntegrationsRequired);
    const flagSwitch = showsFlag ? (
        <label className="flex items-center gap-3 text-[13px]">
            <span className="font-medium text-slate-900">No External Integrations Required</span>
            <Switch
                checked={flag.noIntegrationsRequired}
                disabled={flag.saving}
                onCheckedChange={flag.setNoIntegrationsRequired}
            />
        </label>
    ) : null;

    let tmsCard = (
        <TmsCard defaultIntegrations={defaultIntegrations} disabled={showsFlag && flag.noIntegrationsRequired} />
    );
    if (defaultIntLoading) tmsCard = <span className={`${NOTICE_CLASS} text-slate-500`}>Loading…</span>;
    if (defaultIntError)
        tmsCard = <span className={`${NOTICE_CLASS} text-destructive`}>Couldn&apos;t load the integrations.</span>;

    return (
        <div className="onward-design">
            <section className="flex flex-col gap-3 p-6 pt-[var(--tab-top-padding,1.5rem)]">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <h3 className="m-0 border-l-4 border-primary pl-2 text-[15px] font-bold text-slate-900">
                        Integrations
                    </h3>
                    {flagSwitch}
                </div>
                {/* Two independent columns, so the TMS card can grow without moving the cards on the left. */}
                <div className="grid items-start gap-4 lg:grid-cols-2">
                    <div className="flex flex-col gap-4">
                        <PlatformCard />
                        {isIntegrated && <ShipperIntegrationsCard rows={shipperRows} loading={intLoading} />}
                    </div>
                    {tmsCard}
                </div>
            </section>
        </div>
    );
};

export default Integrations;
